import { useHead } from "#imports";

interface UseServiceSchemaOptions {
  /** The service type label (e.g. "RAG Development", "Multi-Modal AI Development") */
  serviceType: string;
  /** Page / service heading used as the schema `name` */
  heading: string;
  /** FAQ items array */
  faqItems: Array<{ title: string; description: string }>;
  /** Page heading for the last breadcrumb item */
  breadcrumbName: string;
}

/**
 * Shared JSON-LD schema composable for AI service pages.
 * Emits Service, FAQPage, Organization, and BreadcrumbList structured data.
 */
export function useServiceSchema({ serviceType, heading, faqItems, breadcrumbName }: UseServiceSchemaOptions) {
  const route = useRoute();

  useHead({
    script: [
      {
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: heading,
          serviceType,
          description: heading,
          url: `https://webreinvent.com${route.path}`,
          provider: {
            "@type": "Organization",
            name: "WebReinvent",
            url: "https://webreinvent.com",
          },
        }),
        type: "application/ld+json",
      },
      {
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.title,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.description,
            },
          })),
        }),
        type: "application/ld+json",
      },
      {
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "WebReinvent",
          url: "https://webreinvent.com",
          sameAs: [
            "https://linkedin.com/company/webreinvent",
            "https://twitter.com/webreinvent",
          ],
        }),
        type: "application/ld+json",
      },
      {
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://webreinvent.com",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Services",
              item: "https://webreinvent.com/services",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "AI",
              item: "https://webreinvent.com/ai",
            },
            {
              "@type": "ListItem",
              position: 4,
              name: breadcrumbName,
              item: `https://webreinvent.com${route.path}`,
            },
          ],
        }),
        type: "application/ld+json",
      },
    ],
  });
}
